import { ProductizedServicePriceInterval } from '#app/__generated__/api.js';
import { type ProductizedServicePrice_productizedService$key } from '#app/__generated__/relay/ProductizedServicePrice_productizedService.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { graphql, useFragment } from '@contra/relay';
import { forwardRef } from 'react';
import { css, styled } from 'styled-components';

type PRODUCTIZED_SERVICE_PRICE_SIZE = 'lg' | 'sm';

const Container = styled.div<{ $size: PRODUCTIZED_SERVICE_PRICE_SIZE }>`
  ${({ $size, theme }) => css`
    display: flex;
    flex-direction: ${$size === 'lg' ? 'column' : 'row'};
    align-items: center;
    gap: 2px;
    margin-bottom: 0;

    ${theme.mediaQueries.md} {
      margin-bottom: -16px;
    }
  `}
`;

const Price = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 1px;
`;

const PriceText = styled(Text)<{ $size: PRODUCTIZED_SERVICE_PRICE_SIZE }>`
  ${({ $size, theme }) => css`
    align-self: flex-start;
    color: ${theme.colorsV2.black};
    font-weight: 600;
    font-size: ${$size === 'lg' ? 28 : 23}px;
    line-height: ${$size === 'lg' ? 26 : 22}px;
    white-space: nowrap;
  `}
`;

const DollarSign = styled(Text)<{ $size: PRODUCTIZED_SERVICE_PRICE_SIZE }>`
  ${({ $size, theme }) => css`
    align-self: flex-start;
    color: ${theme.colorsV2.black};
    font-weight: 600;
    font-size: ${$size === 'lg' ? 19 : 16}px;
  `}
`;

const SuffixText = styled.span<{ $size: PRODUCTIZED_SERVICE_PRICE_SIZE }>`
  ${({ $size, theme }) => css`
    align-self: flex-end;
    color: ${theme.colorsV2.black};
    font-weight: 600;
    font-size: ${$size === 'lg' ? 19 : 16}px;
    white-space: nowrap;
  `}
`;

const getSuffix = (interval: null | ProductizedServicePriceInterval) => {
  switch (interval) {
    case 'DAY':
      return '/day';
    case 'HOUR':
      return '/hr';
    case 'MONTH':
      return '/mo';
    case 'WEEK':
      return '/wk';
    default:
      return null;
  }
};

type ProductizedServicePriceProps = {
  readonly productizedServiceRef: ProductizedServicePrice_productizedService$key;
  readonly size?: PRODUCTIZED_SERVICE_PRICE_SIZE;
};

export const ProductizedServicePrice = forwardRef<
  HTMLDivElement,
  ProductizedServicePriceProps
>(({ productizedServiceRef, size = 'lg' }, ref) => {
  const {
    price: { amount, interval },
    productizedServiceType,
  } = useFragment<ProductizedServicePrice_productizedService$key>(
    graphql`
      fragment ProductizedServicePrice_productizedService on ProductizedService {
        price {
          amount
          interval
          type
        }
        productizedServiceType
      }
    `,
    productizedServiceRef,
  );
  const numberFormat = Intl.NumberFormat('en-US');
  const priceText =
    amount === null ? 'Contact for pricing' : numberFormat.format(amount);
  const hasAmount = amount !== null;
  const suffix = hasAmount && getSuffix(interval);
  const shouldShowStartingAt =
    size === 'lg' && hasAmount && productizedServiceType === 'NOT_PURCHASABLE';

  return (
    <Container
      $size={size}
      ref={ref}
    >
      {shouldShowStartingAt && <Text textStyle="overline">Starting at</Text>}
      <Price>
        {hasAmount && <DollarSign $size={size}>$</DollarSign>}
        <PriceText $size={size}>{priceText}</PriceText>
        {suffix && <SuffixText $size={size}>{suffix}</SuffixText>}
      </Price>
    </Container>
  );
});
